import type { ApolloError } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import type { Maybe } from 'src/types';
import {
  GetProfileVisibilityDocument,
  UpdateProfileVisibilityDocument,
  type VisibilityLevel,
} from 'src/types/graphql';

import { useCandidateId } from '../data';

import translations from './.vocab';
import {
  updateProfileVisibility,
  updateProfileVisibilityFailure,
  updateProfileVisibilitySuccess,
} from './actionCreators';
import {
  generateUpdateProfileVisibilityOptimisticResponse,
  updateCacheUpdateProfileVisibility,
} from './apolloCache';

export const useGetProfileVisibilityLevel = () => {
  const { language } = useAppConfig();
  const { data, loading, error } = useQuery(GetProfileVisibilityDocument, {
    variables: { languageCode: language },
  });

  const profileVisibilityLevel =
    loading || error ? null : data?.viewer?.profileVisibility?.level || null;

  return { profileVisibilityLevel, loading, error };
};

/** Returns a mock response for use with the apollo MockedProvider */
export const mockGetProfileVisibilityRequest = (
  level: Maybe<VisibilityLevel>,
) => ({
  request: {
    query: GetProfileVisibilityDocument,
    variables: { languageCode: 'en' },
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        profileVisibility: level
          ? { level, __typename: 'ProfileVisibility' }
          : null,
      },
    },
  },
});

const key = 'profile-visibility';

export const useUpdateProfileVisibility = () => {
  const { t } = useTranslations(translations);
  const { candidateId } = useCandidateId();
  const dispatch = useDispatch();

  const onCompleted = () => {
    dispatch(updateProfileVisibilitySuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(updateProfileVisibilityFailure(error));
  };

  return useToastMutation(UpdateProfileVisibilityDocument, {
    key,
    errorActionRouteParameters: { to: 'profile-visibility', mode: 'READ' },
    onMutate: ({ profileVisibility }) => {
      dispatch(updateProfileVisibility(profileVisibility.level));
    },
    onCompleted,
    onError,
    successToastMessage: t('Profile visibility updated'),
    update: updateCacheUpdateProfileVisibility,
    optimisticResponse: generateUpdateProfileVisibilityOptimisticResponse(
      candidateId || '',
    ),
  });
};
